import { Capacitor } from '@capacitor/core';
import { resolveApiUrl } from './resolve-api-url';

/**
 * Bring a browser's borradh.io session across to borrah.com, once.
 *
 * The session cookie from before the 2026-10-04 domain move is scoped to
 * `.borradh.io` and is never sent here, so a browser opening the dashboard on
 * app.borrah.com for the first time looks signed out even though its session
 * is still valid. The API can move it across (`apps/api/src/auth/
 * domain-handoff.core.ts`): a short chain of redirects through the old host
 * that ends back on the requested page with a `.borrah.com` cookie, or with no
 * session if there was nothing to move.
 *
 * This decides whether to start that chain. Only:
 *   - in a browser (the native apps use a bearer token and were never signed
 *     out), on app.borrah.com, talking to api.borrah.com;
 *   - before HANDOFF_UNTIL — the old cookies are 7-day cookies, so after that
 *     there is nothing left to move;
 *   - and once per browser. The marker is written BEFORE navigating, so a
 *     handoff that finds no session can never loop, and it is also written
 *     whenever a session is seen, so a browser that has been signed in here is
 *     never sent through it again (otherwise signing out could be undone by
 *     an old, still-valid borradh.io session).
 */

export const SESSION_SEEN_KEY = 'borrah.session-seen';
export const HANDOFF_HOST = 'app.borrah.com';
export const HANDOFF_API_ORIGIN = 'https://api.borrah.com';
/** 15 days after the move: comfortably past the last 7-day cookie. */
export const HANDOFF_UNTIL = Date.UTC(2026, 9, 19);

export interface DomainHandoffEnv {
  now: number;
  hostname: string;
  isNative: boolean;
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  apiUrl: (path: string) => string;
  navigate: (url: string) => void;
}

const browserEnv = (): DomainHandoffEnv => ({
  now: Date.now(),
  hostname: window.location.hostname,
  isNative: Capacitor.isNativePlatform(),
  storage: (() => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  })(),
  apiUrl: resolveApiUrl,
  navigate: (url) => window.location.assign(url),
});

/** Record that this browser has a session on the new domain. */
export function markSessionSeen(
  env: Pick<DomainHandoffEnv, 'storage'> = browserEnv()
): void {
  try {
    env.storage?.setItem(SESSION_SEEN_KEY, '1');
  } catch {
    // Private mode or storage disabled: the handoff then never starts either.
  }
}

/**
 * Start the handoff if this browser qualifies. Returns true when it has
 * navigated away, in which case the caller must not render or redirect.
 */
export function startDomainHandoffOnce(
  nextPath: string,
  env: DomainHandoffEnv = browserEnv()
): boolean {
  if (env.isNative) return false;
  if (env.hostname !== HANDOFF_HOST) return false;
  if (env.now >= HANDOFF_UNTIL) return false;

  let url: string;
  try {
    url = env.apiUrl(
      `auth/domain-handoff?next=${encodeURIComponent(nextPath)}`
    );
    if (new URL(url).origin !== HANDOFF_API_ORIGIN) return false;
  } catch {
    return false;
  }

  try {
    if (!env.storage || env.storage.getItem(SESSION_SEEN_KEY)) return false;
    // Before navigating: whatever happens next, this browser does it once.
    env.storage.setItem(SESSION_SEEN_KEY, '1');
  } catch {
    return false;
  }

  env.navigate(url);
  return true;
}

/** A promise that never settles, for a route guard that has navigated away. */
export const navigatingAway = (): Promise<never> => new Promise(() => {});
